import React, { memo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tooltip,
  alpha,
} from '@mui/material';
import { ScheduleItem } from '../types/scheduleItem.types';
import { getDateRangeForView, formatTime, isToday } from '../utils/timeUtils';
import {
  getScheduleLinkColors,
} from '../utils/dateTypeColors';
import { formatWoDateForDisplay } from '../utils/woDateFilter';

interface MonthViewProps {
  currentDate: Date;
  items: ScheduleItem[];
  onDateClick?: (date: Date) => void;
  onItemClick?: (item: ScheduleItem) => void;
}

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function buildMonthItemTooltip(item: ScheduleItem): React.ReactNode {
  const lines: string[] = [
    `${formatTime(item.startDateTime)} - ${item.title}`,
  ];
  if (item.customerName) {
    lines.push(`Customer: ${item.customerName}`);
  }
  const pickup = formatWoDateForDisplay(item.woPickupDate);
  if (pickup) {
    lines.push(`Pickup: ${pickup}`);
  }
  const delivery = formatWoDateForDisplay(item.woDeliveryDate);
  if (delivery) {
    lines.push(`Delivery: ${delivery}`);
  }

  return (
    <Box sx={{ whiteSpace: 'pre-line', fontSize: '0.75rem' }}>
      {lines.join('\n')}
    </Box>
  );
}

function buildMoreItemsTooltip(items: ScheduleItem[]): React.ReactNode {
  return (
    <Box
      sx={{
        maxHeight: 220,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
      }}
    >
      {items.map((item) => (
        <Box key={item.scheduleItemId || `${item.workOrderId}-${item.startDateTime?.getTime()}`}>
          {buildMonthItemTooltip(item)}
        </Box>
      ))}
    </Box>
  );
}

export const MonthView: React.FC<MonthViewProps> = memo(({
  currentDate,
  items,
  onDateClick,
  onItemClick,
}) => {
  const { start, end, dates } = getDateRangeForView(currentDate, 'month');
  
  // Group items by date
  const itemsByDate = React.useMemo(() => {
    const grouped = new Map<string, ScheduleItem[]>();
    for (const item of items) {
      const itemDate = item.startDateTime;
      
      // Check if itemDate is a valid Date object
      if (!(itemDate instanceof Date) || isNaN(itemDate.getTime())) {
        continue;
      }
      
      const dateKey = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}-${String(itemDate.getDate()).padStart(2, '0')}`;
      
      if (!grouped.has(dateKey)) {
        grouped.set(dateKey, []);
      }
      grouped.get(dateKey)!.push(item);
    }
    
    return grouped;
  }, [items]);

  // Calculate weeks
  const weeks = React.useMemo(() => {
    const weeks: Date[][] = [];
    let currentWeek: Date[] = [];
    
    for (const date of dates) {
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
      currentWeek.push(date);
    }
    
    if (currentWeek.length > 0) {
      weeks.push(currentWeek);
    }
    
    return weeks;
  }, [dates]);

  const monthLabel = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {monthLabel}
        </Typography>
      </Box>

      {/* Day names */}
      <Box sx={{ display: 'flex', borderBottom: 1, borderColor: 'divider' }}>
        {dayNames.map((day) => (
          <Box
            key={day}
            sx={{
              flex: 1,
              p: 1,
              textAlign: 'center',
              fontWeight: 600,
              fontSize: '0.875rem',
              color: 'text.secondary',
            }}
          >
            {day}
          </Box>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {weeks.map((week, weekIndex) => (
          <Box key={weekIndex} sx={{ display: 'flex', flex: 1 }}>
            {week.map((date) => {
              const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
              const dayItems = itemsByDate.get(dateKey) || [];
              const isCurrentMonth = date.getMonth() === currentDate.getMonth();
              const today = isToday(date);

              return (
                <Box
                  key={dateKey}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    borderBottom: '1px solid',
                    borderTop: today ? '2px solid' : 'none',
                    borderTopColor: today ? 'primary.main' : 'transparent',
                    bgcolor: 'background.paper',
                    opacity: isCurrentMonth ? 1 : 0.5,
                    p: 0.5,
                    minHeight: 100,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    '&:last-child': {
                      borderRight: 'none',
                    },
                  }}
                  onClick={() => onDateClick?.(date)}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: today ? 'primary.main' : 'transparent',
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: today ? 700 : 400,
                        color: today ? 'primary.contrastText' : 'text.primary',
                        fontSize: '0.875rem',
                      }}
                    >
                      {date.getDate()}
                    </Typography>
                  </Box>

                  {/* Schedule items as rows */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, overflow: 'hidden' }}>
                    {(() => {
                      const sortedDayItems = [...dayItems].sort(
                        (a, b) => a.startDateTime.getTime() - b.startDateTime.getTime(),
                      );
                      const visibleItems = sortedDayItems.slice(0, 3);
                      const hiddenItems = sortedDayItems.slice(3);

                      return (
                        <>
                          {visibleItems.map((item) => {
                            const linkStyle = getScheduleLinkColors(item.workOrderId);
                            return (
                              <Tooltip
                                key={item.scheduleItemId}
                                title={buildMonthItemTooltip(item)}
                                arrow
                                placement="top"
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    px: 0.5,
                                    py: 0.25,
                                    borderRadius: 0.5,
                                    bgcolor: alpha(linkStyle.color, 0.15),
                                    borderLeft: 2,
                                    borderColor: linkStyle.color,
                                    cursor: 'pointer',
                                    overflow: 'hidden',
                                    '&:hover': {
                                      bgcolor: alpha(linkStyle.color, 0.25),
                                    },
                                    transition: 'background-color 0.2s',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onItemClick?.(item);
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.65rem',
                                      fontWeight: 500,
                                      color: linkStyle.color,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {formatTime(item.startDateTime)}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontSize: '0.65rem',
                                      color: 'text.primary',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      flex: 1,
                                    }}
                                  >
                                    {item.title}
                                  </Typography>
                                </Box>
                              </Tooltip>
                            );
                          })}
                          {hiddenItems.length > 0 && (
                            <Tooltip
                              title={buildMoreItemsTooltip(hiddenItems)}
                              arrow
                              placement="top"
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    maxWidth: 320,
                                  },
                                },
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '0.65rem',
                                  color: 'text.secondary',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                  },
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDateClick?.(date);
                                }}
                              >
                                +{hiddenItems.length} more
                              </Typography>
                            </Tooltip>
                          )}
                        </>
                      );
                    })()}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Paper>
  );
});

MonthView.displayName = 'MonthView';

